// ./src/components/features/Users/UsersList/UsersList.tsx
//
// List component for the Users page.
// Renders users as custom UserCard items with swipeable actions on mobile
// and hover actions on desktop.
//
// Layout design (desktop, sm+):
//   ┌──────────────────────────────────────────────────────────┐
//   │  [Avatar]  Full Name              Role Badges   [⟳] [🗑] │
//   │           ┌─────────────────┬─────────────────┐          │
//   │           │ 🔑 Username     │ ✉ Email         │          │
//   │           │ demo-viewer     │ demo@car.com ✓  │          │
//   │           └─────────────────┴─────────────────┘          │
//   └──────────────────────────────────────────────────────────┘
//
// Layout design (mobile, <sm):
//   ┌──────────────────────────┐
//   │  [Avatar]  Full Name     │  ← swipeable for actions
//   │  Role Badges             │
//   │  🔑 Username             │
//   │  ✉ Email          ✓     │
//   └──────────────────────────┘
//
// Context support:
// - When rendered inside a <UsersProvider>, data props are read from context.
// - Props can still override context values if provided.

import React, { useMemo, useCallback } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Badge,
  Box,
  ActionIcon,
  Tooltip,
  SimpleGrid,
  Skeleton,
  Table,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";

import { User, UserAccount } from "@/utils/api";
import { useApp, useAppEnvironment } from "@/contexts";
import { useOptionalUsersContext } from "@/contexts";
import { buildFullName } from "@/utils";
import { USER_ROLES, canonicalRoleId } from "@/config/userRoles";
import {
  AppIcon,
  EmptyState,
  ErrorAlert,
  LoadMoreButton,
  SwipeableCard,
  SwipeAction,
  StatusBadge,
  useSwipeableList,
  CardSkeletonActions,
  CardSkeletonLines,
  countCardActions,
} from "@/components/generic";
import {
  useEntranceAnimation,
  entranceListVariants,
  entranceCardVariants,
} from "@/hooks/useEntranceAnimation";

// ============================================
// Types
// ============================================

export interface UsersListProps {
  /**
   * Array of users to display.
   * Optional when inside a UsersProvider — reads from context.
   */
  users?: User[];

  /**
   * Loading state for initial load.
   * Optional when inside a UsersProvider — reads from context.
   */
  loading?: boolean;

  /** Loading state for loading more items. Optional — reads from context. */
  loadingMore?: boolean;

  /** Whether there are more items to load. Optional — reads from context. */
  hasMore?: boolean;

  /** Callback to load more items. Optional — reads from context. */
  onLoadMore?: () => void;

  /** Callback when edit action is clicked */
  onEdit?: (user: User) => void;

  /** Callback when delete action is clicked */
  onDelete?: (user: User) => void;

  /** ID of user currently being removed. Optional — reads from context. */
  removingId?: string | null;

  /** ID of recently edited/added user to highlight */
  highlightedId?: string | null;

  /** Callback when add/invite button is clicked (for empty state) */
  onAdd?: () => void;
  /**
   * A search term or filter is narrowing the list. An empty result then means "no match" — NOT
   * "nothing here yet", so the first-run Add prompt would be telling somebody who just searched
   * that their records are gone. ⚠ The list cannot see the page's search box; this is a PROP.
   */
  hasQueryNarrowing?: boolean;
}

// ============================================
// CSS for hover actions (injected once)
// ============================================

const ACTIONS_CLASS = "user-card__actions";
const CARD_CLASS = "user-card";

const CARD_STYLES = `
  .${ACTIONS_CLASS} {
    opacity: 0;
    pointer-events: none;
    transition: opacity 150ms ease;
  }
  @media (hover: hover) {
    .${CARD_CLASS}:hover .${ACTIONS_CLASS} {
      opacity: 1;
      pointer-events: auto;
    }
  }
  .${CARD_CLASS}:focus-within .${ACTIONS_CLASS} {
    opacity: 1;
    pointer-events: auto;
  }
  @media (hover: none) {
    .${ACTIONS_CLASS} {
      opacity: 1;
      pointer-events: auto;
    }
  }
`;

let stylesInjected = false;
const injectStyles = () => {
  if (stylesInjected || typeof document === "undefined") return;
  const style = document.createElement("style");
  style.setAttribute("data-user-card", "");
  style.textContent = CARD_STYLES;
  document.head.appendChild(style);
  stylesInjected = true;
};

// ============================================
// Loading Skeleton
// ============================================

// ⓘ `actions` = the tablet card's hover buttons (edit / remove), drawn tablet-only: a phone swipes (▶ check-card-skeletons).
// ⓘ The name row is ONE line on a phone too: it wraps the role / status badges under the name only past ~13 characters
// (measured, ROADMAP #127), and most names are 10–11. A long name makes that card 30px taller than this.
const LoadingSkeleton: React.FC<{ actions: number }> = ({ actions }) => {
  return (
    <Stack gap="md">
      {Array.from({ length: 4 }).map((_, index) => (
        <Paper key={index} p="md" withBorder>
          <Group gap="md" wrap="nowrap" align="flex-start">
            <Skeleton height={44} width={44} circle style={{ flexShrink: 0 }} />
            <Stack gap="sm" style={{ flex: 1 }}>
              <Group justify="space-between" wrap="nowrap" gap="sm">
                <CardSkeletonLines count={1} sizes={['lg']} />
                <CardSkeletonActions count={actions} />
              </Group>
              {/* Not a text row: the info-field grid. A typical member shows ONE field (Email — the Login is hidden when
                  it equals the email), and a field is two lines: its label over its value. */}
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs" verticalSpacing="xs">
                <Skeleton height={41} width="70%" />
              </SimpleGrid>
            </Stack>
          </Group>
        </Paper>
      ))}
    </Stack>
  );
};

// ============================================
// Avatar Circle
// ============================================

interface AvatarCircleProps {
  name: string;
  color?: string;
}

const AvatarCircle: React.FC<AvatarCircleProps> = ({ name, color = "violet" }) => {
  const initials = useMemo(() => {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return (name[0] || "?").toUpperCase();
  }, [name]);

  return (
    <Box
      style={{
        width: 44,
        height: 44,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: `var(--mantine-color-${color}-0)`,
        border: `2px solid var(--mantine-color-${color}-2)`,
        flexShrink: 0,
      }}
    >
      <Text size="md" fw={600} c={color}>
        {initials}
      </Text>
    </Box>
  );
};

// ============================================
// Info Field — icon + label + value
// ============================================

interface InfoFieldProps {
  icon: string;
  label: string;
  value: string;
  suffix?: React.ReactNode;
}

const InfoField: React.FC<InfoFieldProps> = ({ icon, label, value, suffix }) => (
  <Group gap={8} wrap="nowrap" align="flex-start" style={{ minWidth: 0 }}>
    <Group gap={4} wrap="nowrap" align="center" style={{ flexShrink: 0, height: 18 }}>
      <AppIcon iconName={icon} size={14} />
    </Group>
    <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={500} lh={1.3}>
        {label}
      </Text>
      <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
        <Text size="md" truncate style={{ minWidth: 0 }}>
          {value}
        </Text>
        {suffix}
      </Group>
    </Stack>
  </Group>
);

// ============================================
// Single User Card
// ============================================

interface UserCardProps {
  user: User;
  onClick?: (user: User) => void;
  onEdit?: (user: User) => void;
  onDelete?: (user: User) => void;
  highlighted: boolean;
  isDeleting: boolean;
  isProtected: boolean;
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const UserCardComponent: React.FC<UserCardProps> = ({
  user,
  //onClick,
  onEdit,
  onDelete,
  highlighted,
  isDeleting,
  isProtected,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["users", "common"]);

  injectStyles();

  const fullName = buildFullName(user) || user.email || user.username || "—";
  const showUsername = user.username && user.username !== user.email;
  const hasActions = !isProtected && (!!onEdit || !!onDelete);

  // Effective action callbacks (null if protected)
  const effectiveOnEdit = isProtected ? undefined : onEdit;
  const effectiveOnDelete = isProtected ? undefined : onDelete;

  const roles = useMemo(() => {
    if (!user.userAccounts || user.userAccounts.length === 0) return null;
    return user.userAccounts
      .map((ua: UserAccount) => ua.userRole?.roleName)
      .filter(Boolean);
  }, [user.userAccounts]);

  // ── Handlers ──

  // const handleClick = useCallback(() => {
  //   onClick?.(user);
  // }, [onClick, user]);

  const handleEditClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      effectiveOnEdit?.(user);
    },
    [effectiveOnEdit, user]
  );

  const handleDeleteClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      effectiveOnDelete?.(user);
    },
    [effectiveOnDelete, user]
  );

  // ── Swipe actions (mobile) ──

  const leftSwipeActions = useMemo((): SwipeAction[] => {
    if (isProtected) return [];
    const actions: SwipeAction[] = [];
    if (onEdit) {
      actions.push({
        key: "edit",
        icon: "Edit",
        label: t("common:buttons.edit"),
        color: "blue",
        onClick: () => onEdit(user),
        disabled: isDeleting,
      });
    }
    if (onDelete) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("common:buttons.delete"),
        color: "red",
        onClick: () => onDelete(user),
        loading: isDeleting,
        disabled: isDeleting,
      });
    }
    return actions;
  }, [isProtected, onEdit, onDelete, user, isDeleting, t]);

  // ── Hover actions (desktop) ──

  const hoverActionsEl = hasActions && (
    <Group gap={4} wrap="nowrap" className={ACTIONS_CLASS}>
      {effectiveOnEdit && (
        <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="blue"
            size="md"
            onClick={handleEditClick}
            disabled={isDeleting}
            aria-label={t("common:buttons.edit")}
          >
            <AppIcon iconName="Edit" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {effectiveOnDelete && (
        <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="red"
            size="md"
            onClick={handleDeleteClick}
            loading={isDeleting}
            disabled={isDeleting}
            aria-label={t("common:buttons.delete")}
          >
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );

  // ── Email verified badge ──

  const emailVerifiedBadge = user.emailConfirmation ? (
    <Badge size="xs" color="blue" variant="light">
      {t("users:list.verified", { defaultValue: "Verified" })}
    </Badge>
  ) : null;

  // ── Card content ──

  const cardContent = (
    <Paper
      p="md"
      withBorder
      bg={highlighted ? "green.0" : undefined}
      opacity={isDeleting ? 0.5 : 1}
      className={CARD_CLASS}
      style={{
        //cursor: onClick ? "pointer" : undefined,
        transition: "box-shadow 150ms ease, opacity 150ms ease",
      }}
    //onClick={handleClick}
    >
      <Group gap="md" wrap="nowrap" align="flex-start">
        {/* Avatar */}
        <AvatarCircle name={fullName} />

        {/* Content */}
        <Stack gap="sm" style={{ flex: 1, minWidth: 0 }}>
          {/* Row 1: Name + Roles + Status + Actions */}
          <Group justify="space-between" wrap="nowrap" gap="sm">
            <Group gap="sm" wrap="wrap" style={{ flex: 1, minWidth: 0 }}>
              <Text fw={600} size="lg" truncate style={{ minWidth: 0 }}>
                {fullName}
              </Text>
              {roles && roles.map((roleName, index) => (
                <Badge key={index} variant="light" size="sm" color="violet">
                  {roleName}
                </Badge>
              ))}
              {user.status != null && (
                <StatusBadge status={user.status} />
              )}
            </Group>
            {/* Desktop hover actions */}
            <Box visibleFrom="sm" style={{ flexShrink: 0 }}>
              {hoverActionsEl}
            </Box>
          </Group>

          {/* Row 2: Info fields in two-column grid on desktop */}
          {(showUsername || user.email) && (
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs" verticalSpacing="xs">
              {showUsername && (
                <InfoField
                  icon="Key"
                  label={t("users:list.username", { defaultValue: "Login" })}
                  value={user.username!}
                />
              )}
              {user.email && (
                <InfoField
                  icon="Mail"
                  label={t("users:list.email", { defaultValue: "Email" })}
                  value={user.email}
                  suffix={emailVerifiedBadge}
                />
              )}
            </SimpleGrid>
          )}
        </Stack>
      </Group>
    </Paper>
  );

  // ── Mobile: swipeable / Desktop: plain ──

  if (leftSwipeActions.length > 0) {
    return (
      <>
        <Box hiddenFrom="sm">
          <SwipeableCard
            leftSwipeActions={leftSwipeActions}
            forceClose={forceClose}
            onForceCloseComplete={onForceCloseComplete}
            onSwipeStateChange={onSwipeStateChange}
            disabled={isDeleting}
            borderRadius="var(--mantine-radius-default)"
          >
            {cardContent}
          </SwipeableCard>
        </Box>
        <Box visibleFrom="sm">
          {cardContent}
        </Box>
      </>
    );
  }

  return cardContent;
};

const UserCard = React.memo(UserCardComponent);

// ============================================
// Desktop table row
// ============================================
//
// ⚠⚠ A LIST PAGE SHOWS A TABLE ON LARGE SCREENS AND CARDS BELOW — this one showed cards at every
// width (owner, 2026-09-28). Cards are a PHONE affordance: one record per full-width block, tall
// enough to hold its own labels. On a desktop that turns a twenty-person team into a page of
// scrolling, with the same four values re-labelled on every row, and it stops matching every other
// list in the product. `UserInvitationsList` had the same gap; every other list already had a table.

interface UserRowProps {
  user: User;
  onEdit?: (user: User) => void;
  onDelete?: (user: User) => void;
  highlighted: boolean;
  isDeleting: boolean;
  isProtected: boolean;
}

const UserTableRow: React.FC<UserRowProps> = ({
  user,
  onEdit,
  onDelete,
  highlighted,
  isDeleting,
  isProtected,
}) => {
  const { t } = useTranslation(["users", "common"]);

  const fullName = buildFullName(user) || user.email || user.username || "\u2014";
  const showUsername = user.username && user.username !== user.email;

  // ⚠ The SAME protection rule the card uses — the account owner cannot be edited or removed, and a
  // row that offered the buttons anyway would be a refusal waiting at the end of a click.
  const canEdit = !isProtected && !!onEdit;
  const canDelete = !isProtected && !!onDelete;

  const roles = useMemo(() => {
    if (!user.userAccounts || user.userAccounts.length === 0) {
      return null;
    }

    return user.userAccounts.map((ua: UserAccount) => ua.userRole?.roleName).filter(Boolean);
  }, [user.userAccounts]);

  return (
    <Table.Tr
      style={{
        opacity: isDeleting ? 0.5 : 1,
        backgroundColor: highlighted ? "var(--mantine-color-green-0)" : undefined,
        transition: "background-color 150ms ease, opacity 150ms ease",
      }}
    >
      <Table.Td style={{ width: 52 }}>
        <AvatarCircle name={fullName} />
      </Table.Td>

      <Table.Td>
        <Text size="md" fw={600} lineClamp={1}>
          {fullName}
        </Text>
        {showUsername && (
          <Text size="sm" c="dimmed" lineClamp={1}>
            {user.username}
          </Text>
        )}
      </Table.Td>

      <Table.Td>
        <Group gap="xs" wrap="nowrap">
          <Text size="md" lineClamp={1}>
            {user.email || "\u2014"}
          </Text>
          {user.emailConfirmation && (
            <Badge size="sm" color="blue" variant="light" style={{ flexShrink: 0 }}>
              {t("users:list.verified", { defaultValue: "Verified" })}
            </Badge>
          )}
        </Group>
      </Table.Td>

      <Table.Td style={{ width: 180 }}>
        <Group gap={4} wrap="wrap">
          {roles?.map((roleName, index) => (
            <Badge key={index} variant="light" size="sm" color="violet">
              {roleName}
            </Badge>
          ))}
        </Group>
      </Table.Td>

      <Table.Td style={{ width: 120 }}>
        {user.status != null && <StatusBadge status={user.status} />}
      </Table.Td>

      <Table.Td style={{ width: 96 }}>
        <Group gap={4} wrap="nowrap">
          {canEdit && (
            <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
              <ActionIcon
                variant="subtle"
                color="blue"
                size="md"
                onClick={() => onEdit?.(user)}
                disabled={isDeleting}
                aria-label={t("common:buttons.edit")}
              >
                <AppIcon iconName="Edit" size={16} />
              </ActionIcon>
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
              <ActionIcon
                variant="subtle"
                color="red"
                size="md"
                onClick={() => onDelete?.(user)}
                loading={isDeleting}
                disabled={isDeleting}
                aria-label={t("common:buttons.delete")}
              >
                <AppIcon iconName="Trash" size={16} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      </Table.Td>
    </Table.Tr>
  );
};

/** ⚠ Column widths mirror the real row, so the table does not jump when the data lands. */
const UserTableRowSkeleton: React.FC = () => (
  <Table.Tr>
    <Table.Td style={{ width: 52 }}>
      <Skeleton height={36} width={36} circle />
    </Table.Td>
    <Table.Td>
      <Skeleton height={16} width={160} />
    </Table.Td>
    <Table.Td>
      <Skeleton height={16} width="70%" maw={240} />
    </Table.Td>
    <Table.Td style={{ width: 180 }}>
      <Skeleton height={20} width={90} radius="xl" />
    </Table.Td>
    <Table.Td style={{ width: 120 }}>
      <Skeleton height={20} width={70} radius="xl" />
    </Table.Td>
    <Table.Td style={{ width: 96 }}>
      <Skeleton height={20} width={56} />
    </Table.Td>
  </Table.Tr>
);

// ============================================
// Main Component
// ============================================

const UsersListComponent: React.FC<UsersListProps> = ({
  users: usersProp,
  loading: loadingProp,
  loadingMore: loadingMoreProp,
  hasMore: hasMoreProp,
  onLoadMore: onLoadMoreProp,
  onEdit,
  onDelete,
  removingId: removingIdProp,
  highlightedId,
  onAdd,
  hasQueryNarrowing = false,
}) => {
  const { t } = useTranslation(["users", "common"]);
  const { user: currentUser } = useApp();

  // ── Context vs Props resolution ──
  const usersContext = useOptionalUsersContext();

  // ⓘ Memoised: a fresh `[]` on every render would change what the memos below depend on (#129).
  const users = useMemo(() => usersProp ?? usersContext?.users ?? [], [usersProp, usersContext?.users]);
  const loading = loadingProp ?? usersContext?.loading ?? false;
  const loadingMore = loadingMoreProp ?? usersContext?.loadingMore ?? false;
  const { isDesktop } = useAppEnvironment();

  /**
   * ⚠⚠ "NOT LOADED YET" IS NOT "EMPTY", AND TELLING THEM APART IS THE WHOLE FIX.
   *
   * This list used to decide on `!loading && users.length === 0`, which is TRUE on the very first
   * render — before the request has even started. The page therefore painted "No users found",
   * then the skeleton, then the table (owner, 2026-09-28: "it shows No Users, then loading skeleton
   * then table with users"). Purchase Orders never did it because its provider auto-loads on mount,
   * so `loading` was already true by the first paint.
   * ⓘ Branching on `isLoaded` makes the page correct whatever starts the load and whenever it
   * starts: until the first read has COME BACK, the honest answer is a skeleton.
   * ⚠ A CALLER THAT PASSES `users` AS A PROP HAS ALREADY LOADED THEM. This component supports both
   * a provider and plain props, and defaulting to `false` without that fallback would leave a
   * props-only caller staring at a skeleton for ever — with no provider to ever flip the flag.
   */
  const isLoaded = usersContext?.isLoaded ?? usersProp !== undefined;

  /**
   * ⚠⚠ A FAILED READ IS NOT AN EMPTY LIST. `list()` sets `isLoaded` true and the array to [] on both
   * the error and the exception path, so a read that failed rendered "No users found — invite team
   * members to get started": the host is told their team does not exist, nothing they do fixes it,
   * and nothing says why. The order is error → no-match → first-run; this list had no first branch.
   * ⚠ No `onDismiss`: dismissing would restore the lie.
   */
  const lastError = usersContext?.lastError ?? null;
  const readFailed = isLoaded && users.length === 0 && !!lastError;
  const hasMore = hasMoreProp ?? usersContext?.hasMore ?? false;
  const onLoadMore = onLoadMoreProp ?? usersContext?.loadMore;
  const removingId = removingIdProp ?? usersContext?.removingId ?? null;

  // Manage swipeable cards
  const {
    openCard,
    closeAllCards,
    shouldForceClose,
    handleForceCloseComplete,
  } = useSwipeableList();

  // Entrance animation
  const { hasAnimated } = useEntranceAnimation({ itemCount: users.length });

  // ── Protected user detection ──

  const protectedUserIds = useMemo(() => {
    const ids = new Set<string>();
    if (currentUser?.id) ids.add(currentUser.id);

    users.forEach((u) => {
      const hasOwnerRole = u.userAccounts?.some(
        (ua) => canonicalRoleId(ua?.userRoleId) === USER_ROLES.OWNER
      );
      if (hasOwnerRole && u.id) ids.add(u.id);
    });

    return ids;
  }, [users, currentUser?.id]);

  // ── Handlers ──

  const handleEdit = useCallback(
    (user: User) => {
      closeAllCards();
      onEdit?.(user);
    },
    [onEdit, closeAllCards]
  );

  const handleDelete = useCallback(
    (user: User) => {
      closeAllCards();
      onDelete?.(user);
    },
    [onDelete, closeAllCards]
  );

  const handleSwipeStateChange = useCallback(
    (userId: string, isOpen: boolean) => {
      if (isOpen) openCard(userId);
    },
    [openCard]
  );

  // ── Nothing read back yet ── (see `isLoaded` above — NOT the same as "empty")
  //
  // ⚠ ONLY the first read. A REFETCH keeps what is on screen — the rows, or the empty state — and
  // ContentLoadingOverlay says it is reloading. Re-showing the skeleton whenever the list happens
  // to be empty is what made an empty page read skeleton → "none" → skeleton → "none".

  const showSkeleton = !isLoaded;

  // ── A read that FAILED ── never falls through to "nothing here"

  if (!showSkeleton && readFailed) {
    return (
      <ErrorAlert
        error={lastError}
        title={t("users:errors.loadTitle", { defaultValue: "Couldn't load users" })}
        page={t("users:pageName", { defaultValue: "Users" })}
        operation="userList"
      />
    );
  }

  // ── Empty state ── only once we actually know the set is empty

  if (!showSkeleton && users.length === 0) {
    if (hasQueryNarrowing) {
      return (
        <Text size="md" c="dimmed" ta="center" p="md">
          {t("users:emptyState.noMatch", { defaultValue: "No users match your search." })}
        </Text>
      );
    }

    return (
      <Paper withBorder p="xl">
        <EmptyState
          title={t("users:emptyState.title", { defaultValue: "No users found" })}
          description={t("users:emptyState.description", {
            defaultValue: "Invite team members to get started.",
          })}
          iconName="Users"
          actionLabel={t("users:emptyState.actionLabel", { defaultValue: "Invite User" })}
          onAction={onAdd}
          size="md"
        />
      </Paper>
    );
  }

  // ── Desktop: a table, like every other list page ──

  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 52 }} />
                <Table.Th>{t("users:list.name", { defaultValue: "Name" })}</Table.Th>
                <Table.Th>{t("users:list.email", { defaultValue: "Email" })}</Table.Th>
                <Table.Th style={{ width: 180 }}>
                  {t("users:list.roles", { defaultValue: "Roles" })}
                </Table.Th>
                <Table.Th style={{ width: 120 }}>
                  {t("users:list.status", { defaultValue: "Status" })}
                </Table.Th>
                <Table.Th style={{ width: 96 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {showSkeleton
                ? Array.from({ length: 6 }).map((_, i) => <UserTableRowSkeleton key={i} />)
                : users.map((user) => (
                    <UserTableRow
                      key={user.id}
                      user={user}
                      onEdit={onEdit ? handleEdit : undefined}
                      onDelete={onDelete ? handleDelete : undefined}
                      highlighted={user.id === highlightedId}
                      isDeleting={removingId === user.id}
                      isProtected={protectedUserIds.has(user.id || "")}
                    />
                  ))}
            </Table.Tbody>
          </Table>
        </Paper>

        {!showSkeleton && (
          <LoadMoreButton
            hasMore={hasMore}
            loading={loading}
            loadingMore={loadingMore}
            onLoadMore={onLoadMore}
          />
        )}
      </Stack>
    );
  }

  // ── Tablet / phone: cards ──

  if (showSkeleton) {
    return <LoadingSkeleton actions={countCardActions(onEdit, onDelete)} />;
  }

  return (
    <Stack gap="md">
      <motion.div
        variants={hasAnimated ? undefined : entranceListVariants}
        initial={hasAnimated ? false : "hidden"}
        animate={hasAnimated ? undefined : "visible"}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--mantine-spacing-md)",
        }}
      >
        <AnimatePresence mode="sync">
          {users.map((user) => (
            <motion.div
              key={user.id}
              variants={hasAnimated ? undefined : entranceCardVariants}
              initial={hasAnimated ? false : "hidden"}
              animate={hasAnimated ? undefined : "visible"}
              exit="exit"
              layout
            >
              <UserCard
                user={user}
                //onClick={onEdit ? handleUserClick : undefined}
                onEdit={onEdit ? handleEdit : undefined}
                onDelete={onDelete ? handleDelete : undefined}
                highlighted={user.id === highlightedId}
                isDeleting={removingId === user.id}
                isProtected={protectedUserIds.has(user.id || "")}
                forceClose={shouldForceClose(user.id || "")}
                onForceCloseComplete={() =>
                  handleForceCloseComplete(user.id || "")
                }
                onSwipeStateChange={(isOpen) =>
                  handleSwipeStateChange(user.id || "", isOpen)
                }
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      <LoadMoreButton
        hasMore={hasMore}
        loading={loading}
        loadingMore={loadingMore}
        onLoadMore={onLoadMore}
      />
    </Stack>
  );
};

export const UsersList = React.memo(UsersListComponent);